import { RouterLink as NextLink } from "../../router";
import type { DashboardStat } from "@excellent/core";
import { Card, CardContent } from "../../card";
import { StatCard } from "../stat-card";
import { StatGrid } from "./stat-grid";

/**
 * Renders the union of `DashboardStat`s as a responsive grid of StatCards
 * inside the shared card frame (soft tint + hairline, no shadow) — so a
 * dashboard stat strip reads as one finished, anchored panel, consistent with
 * the framed kanban cards / tables / <StatStrip> bands elsewhere, instead of
 * floating bare numbers (the app-wide stat-strip redesign; see
 * docs/design/MINIMAL-SURFACES-PRD.md + /styleguide/stat-strips).
 *
 * Why a framed GRID here (not the single-row <StatStrip> divided band): the
 * shared dashboard builders pass a variable count (3–7 stats), so the layout
 * must wrap. <StatStrip> is the compact-row treatment for ≤5-stat strips that
 * caption a table/board; this is the multi-stat dashboard-panel treatment.
 * Both share the same frame.
 *
 * Caller is responsible for ordering and capping; this component does not
 * mutate the input.
 */
export function DashboardStatGrid({ stats }: { stats: DashboardStat[] }) {
  if (stats.length === 0) return null;
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="px-6 py-5">
        <StatGrid cols={4}>
          {stats.map((s, i) => {
            const card = (
              <StatCard
                title={s.title}
                value={s.value}
                description={s.description}
                footer={s.footer}
                trend={s.trend}
                trendLabel={s.trendLabel}
                trendGood={s.trendGood}
                sparkData={s.sparkData}
                accentColor={s.accentColor}
                interactive={Boolean(s.href)}
              />
            );
            return (
              <div key={`${s.title}-${i}`}>
                {s.href ? <NextLink href={s.href}>{card}</NextLink> : card}
              </div>
            );
          })}
        </StatGrid>
      </CardContent>
    </Card>
  );
}
